// Shared utility layer (TS-2.5.04) - pure, no side effects. Promoted out of
// features/products/lib/groupProjectsByProduct.ts (decisions.md D-277): that file's bucketing
// shape - group items by a product key, resolve each key against the product catalogue, sort
// assigned buckets alphabetically by product NAME, keep a synthetic "Unknown product" bucket for a
// key the catalogue did not return (D-272), and always append exactly one Unassigned bucket last,
// even when empty - is now needed by a THIRD feature (roadmap's Team/Product board toggle) beside
// the two D-271 already named (features/products, features/projects). D-271 itself called the
// promotion "the more correct long-term fix... set aside rather than rejected outright" for
// exactly this reason: a second consumer did not justify it, a third does. This module holds the
// join/bucket shape generically over the bucketed item type; each feature still supplies its own
// key accessor and its own field-name wrapper, so no feature's public shape changes.
import type { ProductRecord } from "../api/generated/contracts";

export interface ProductBucket<T> {
  /** null for the Unassigned bucket; a placeholder record (isActive: false, no owners) for an unknown key. */
  readonly product: ProductRecord | null;
  readonly isUnknown: boolean;
  readonly items: readonly T[];
}

function unknownProductPlaceholder(productKey: string): ProductRecord {
  return {
    key: productKey,
    name: `Unknown product (${productKey})`,
    technologyOwner: null,
    businessOwner: null,
    isActive: false,
    concurrencyToken: null,
    updatedAtUtc: null,
    updatedBy: null,
  };
}

/**
 * Buckets `items` by the product key `getProductKey` reads off each one, resolved against
 * `products`. Assigned buckets sort alphabetically by product name; an Unassigned bucket (product:
 * null) is always appended last, even when empty. An item whose key matches no product in
 * `products` gets a synthetic "Unknown product" bucket rather than being dropped.
 */
export function groupByProduct<T>(
  items: readonly T[],
  products: readonly ProductRecord[],
  getProductKey: (item: T) => string | null,
): readonly ProductBucket<T>[] {
  const byKey = new Map(products.filter((product) => product.key).map((product) => [product.key as string, product]));
  const byProductKey = new Map<string, T[]>();
  const unassigned: T[] = [];

  for (const item of items) {
    const productKey = getProductKey(item);
    if (!productKey) {
      unassigned.push(item);
      continue;
    }
    const bucket = byProductKey.get(productKey) ?? [];
    bucket.push(item);
    byProductKey.set(productKey, bucket);
  }

  const assignedBuckets: ProductBucket<T>[] = [...byProductKey.entries()]
    .map(([productKey, groupItems]) => {
      const product = byKey.get(productKey) ?? unknownProductPlaceholder(productKey);
      return { product, isUnknown: !byKey.has(productKey), items: groupItems };
    })
    .sort((a, b) => (a.product?.name ?? "").localeCompare(b.product?.name ?? ""));

  return [...assignedBuckets, { product: null, isUnknown: false, items: unassigned }];
}
